import { useBooleanState } from '@cian/react-utils';
import { Image } from '@cian/ui-kit';

import { clsx } from 'clsx';
import { type FC, useEffect, useRef } from 'react';

import styles from './BackgroundVideo.css';

export interface IBackgroundVideoProps {
  active: boolean;
  fetchPriority?: 'high' | 'low';
  loading?: 'eager' | 'lazy';
  poster: string;
  url: string;
}

export const BackgroundVideo: FC<IBackgroundVideoProps> = ({ active, fetchPriority, loading, poster, url }) => {
  const { state: isLoaded, setTrue: setLoadedState } = useBooleanState();
  const { state: isError, setTrue: setErrorState } = useBooleanState();

  const videoRef = useRef<HTMLVideoElement>(null);

  const handleLoaded = (): void => setLoadedState();
  const handleError = (): void => {
    setErrorState();
    setLoadedState();
  };

  let preload: 'auto' | 'none' | undefined;

  // V1 не передаёт loading: отсутствие preload сохраняет прежний сетевой контракт.
  if (loading !== undefined) {
    preload = loading === 'eager' || active ? 'auto' : 'none';
  }

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    if (!isLoaded) {
      if (active && loading === 'lazy') {
        videoRef.current.load();
      }

      return;
    }

    if (active) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [active, isLoaded, loading]);

  return (
    <div className={styles['container']} data-testid="background-video-container">
      <video
        loop
        muted
        playsInline
        className={styles['video']}
        data-testid="background-video"
        preload={preload}
        ref={videoRef}
        onCanPlayThrough={handleLoaded}
        onError={handleError}
      >
        <source src={url} type="video/mp4" />
      </video>
      <div
        aria-hidden="true"
        className={clsx(styles['poster'], isLoaded && !isError && styles['poster--hidden'])}
        data-testid="background-video-poster"
      >
        {loading === undefined && fetchPriority === undefined ? (
          <Image alt="" objectFit="cover" src={poster} />
        ) : (
          <img
            alt=""
            className={styles['poster-image']}
            decoding={loading === 'eager' ? 'sync' : 'async'}
            fetchPriority={fetchPriority}
            loading={loading}
            src={poster}
          />
        )}
      </div>
    </div>
  );
};
